import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { switchMap, Observable, catchError, of, filter, take, map } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { UserService } from '../services/user.service';
import { NetworkService } from '../services/network.service';
import { ProductContextService } from '../services/product-context.service';
import { BeneficiaryModel } from '@models/beneficiaries/Beneficiary.model';

export function authGuard(): Observable<boolean | UrlTree> {
  const router: Router = inject(Router);
  const authService: AuthService = inject(AuthService);
  const userService: UserService = inject(UserService);
  const networkService: NetworkService = inject(NetworkService);
  const productContext: ProductContextService = inject(ProductContextService);

  const admitFromCachedProduct = (uid: string): boolean | UrlTree =>
    productContext.readFor(uid) !== 'unknown' ? true : router.createUrlTree(['/auth']);

  const admitFromBeneficiary = (beneficiary: BeneficiaryModel): boolean | UrlTree => {
    const subscriptionId: string | undefined = userService.getActiveSubscriptionEntry(beneficiary.subscriptions)?.subscriptionId;

    return subscriptionId ? true : router.createUrlTree(['/unauthorized']);
  };

  return authService.user$.pipe(
    switchMap(user => {
      if (!user) {
        return of(router.createUrlTree(['/auth']));
      }

      return networkService.online$.pipe(
        take(1),
        switchMap((isOnline: boolean) => {
          /** Deliberately the same connectivity signal the dashboard uses to decide whether to fetch: if the two disagreed, the guard would admit a beneficiary the dashboard then fails to load for. */
          if (!isOnline) {
            return of(admitFromCachedProduct(user.uid));
          }

          return userService.beneficiary$.pipe(
            filter(Boolean),
            take(1),
            map(admitFromBeneficiary),
            /** No timeout on purpose: a merely slow network would be mistaken for a dead one, telling an online beneficiary their balance is unavailable. */
            catchError(() => of(admitFromCachedProduct(user.uid))),
          );
        }),
      );
    }),
    catchError(() => of(router.createUrlTree(['/auth']))),
  );
}

export function noAuthGuard(): Observable<boolean | UrlTree> {
  const router: Router = inject(Router);
  const authService: AuthService = inject(AuthService);
  return authService.user$.pipe(
    switchMap(currentUser => {
      if (!!currentUser) {
        return of(router.createUrlTree(['/']));
      }
      return of(true);
    }),
    catchError(() => {
      return of(true);
    }),
  );
}
